import React, { useState, useEffect, useRef } from "react";
import Lines from "../lines/Lines";
import Racing from "../racing/Racing";
import { socket } from "../../io";
import Bonos from '../bonos/Bonos'
import Jackpots from "../jackpots/Jackpots";
import { Logout } from "../../utils/logout";
import './dashboard-countdown.css';
import './dashboard-countdown-canodrome.css';
import './dashboard-screen-transition.css';
import { useDogs8AudioTest } from './useDogs8AudioTest';

const CountdownScreen = ({ time, run }) => {
  const normalizedTime = Math.max(0, Math.ceil(Number(time) || 0));
  const [seconds, setSeconds] = useState(normalizedTime);
  useEffect(() => {
    setSeconds(normalizedTime);
  }, [normalizedTime]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setSeconds(value => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);
  const progress = Math.min(100, Math.max(0, (seconds / 10) * 100));
  return (
    <main className="race-countdown">
      <div className="race-countdown__speed-lines" />
      <section className="race-countdown__card">
        <header className="race-countdown__header">
          <div className="race-countdown__brand">CORRIENDOG</div>
          <div className="race-countdown__live"><i /> CONTROL DE CARRERA</div>
        </header>
        <div className="race-countdown__body">
          <div className="race-countdown__race-info">
            <span>PRÓXIMA SALIDA</span>
            <strong>CARRERA {run?.num || run?.code || '—'}</strong>
            <small>Apuestas cerradas</small>
          </div>
          <div className="race-countdown__timer" style={{ '--progress': `${progress}%` }}>
            <div className="race-countdown__timer-inner">
              <span>EN PISTA EN</span>
              <strong>{String(seconds).padStart(2, '0')}</strong>
              <small>SEGUNDOS</small>
            </div>
          </div>
          <div className="race-countdown__status">
            <span>ESTADO</span>
            <strong><i /> Galgos preparados</strong>
            <small>La transmisión inicia automáticamente</small>
          </div>
        </div>
        <div className="race-countdown__track" aria-hidden="true">
          {[1,2,3,4,5,6].map(lane => <span key={lane}>{lane}</span>)}
        </div>
        {!time && <button className="race-countdown__exit" onClick={Logout}>Volver a iniciar sesión</button>}
      </section>
    </main>
  );
};

function Dashboard({ paleOnly = false }) {
  const [run, setRun] = useState();
  const [screen, setScreen] = useState(4);
  const [time, setTime] = useState();
  const [paidJp, setPaidJp] = useState(false);
  const [jackpotToPay, setJackpotToPay] = useState({ value: 0, type: '' });
  const payoutTimer = useRef();
  const prizeQueueRef = useRef([]);
  const paidJpRef = useRef(false);
  const transitionTimer = useRef();
  const resultTransitionTimer = useRef();
  const currentScreenRef = useRef(4);
  const [lineTransition, setLineTransition] = useState(0);
  const [resultTransition, setResultTransition] = useState(0);
  const lastPayoutKey = useRef('');
  useDogs8AudioTest({
    screen,
    paidJp,
    jackpotKey: jackpotToPay?.ticket ? `${jackpotToPay.ticket}-${jackpotToPay.type}-${jackpotToPay.value}` : '',
  });

  useEffect(() => {
    const showNextPrize = () => {
      if (paidJpRef.current || prizeQueueRef.current.length === 0) return;
      const prize = prizeQueueRef.current.shift();
      paidJpRef.current = true;
      setJackpotToPay(prize);
      setPaidJp(true);
      window.clearTimeout(payoutTimer.current);
      payoutTimer.current = window.setTimeout(() => {
        paidJpRef.current = false;
        setPaidJp(false);
        showNextPrize();
      }, 10000);
    };

    const enqueuePrize = (prize) => {
      prizeQueueRef.current.push(prize);
      if (currentScreenRef.current === 3) showNextPrize();
    };

    const handleRun = (dataRun) => {
      setRun(dataRun.run);
    };

    const handleGet = (data) => {
      const nextScreen = Number(data?.screen);
      if (currentScreenRef.current === 3 && nextScreen === 1) {
        setLineTransition(value => value + 1);
        window.clearTimeout(transitionTimer.current);
        transitionTimer.current = window.setTimeout(() => setLineTransition(0), 850);
      }
      if (currentScreenRef.current === 2 && nextScreen === 3) {
        setResultTransition(value => value + 1);
        window.clearTimeout(resultTransitionTimer.current);
        resultTransitionTimer.current = window.setTimeout(() => setResultTransition(0), 950);
      }
      if (nextScreen === 3) showNextPrize();
      if (nextScreen) currentScreenRef.current = nextScreen;
      setScreen(value => data && data.screen && data.screen !== value ? data.screen : value);
      setTime(value => data && data.time && data.time !== value ? data.time : value);
    };

    const handleScreen = (data) => {
      const nextScreen = Number(data?.screen);
      if (currentScreenRef.current === 3 && nextScreen === 1) {
        setLineTransition(value => value + 1);
        window.clearTimeout(transitionTimer.current);
        transitionTimer.current = window.setTimeout(() => setLineTransition(0), 850);
      }
      if (currentScreenRef.current === 2 && nextScreen === 3) {
        setResultTransition(value => value + 1);
        window.clearTimeout(resultTransitionTimer.current);
        resultTransitionTimer.current = window.setTimeout(() => setResultTransition(0), 950);
      }
      if (nextScreen === 3) showNextPrize();
      if (nextScreen) currentScreenRef.current = nextScreen;
      setScreen(value => data && data.screen && data.screen !== value ? data.screen : value);
      if (data.time) {
        setTime(value => data.time !== value ? data.time : value);
      }
    };

    const handleJackpot = (data) => {
      if (data && data.winner && data.winner.jackpot && data.winner.jackpot.value > 0) {
        const jackpot = data.winner.jackpot;
        const type = String(jackpot.type || '').toLowerCase();
        const ownPoint = localStorage.getItem('point');
        const ownCustomer = localStorage.getItem('customer');
        const belongsToThisScreen = type.includes('general') ||
          (type.includes('local') && jackpot.point && String(jackpot.point) === String(ownPoint)) ||
          (type.includes('consorcio') && jackpot.customer && String(jackpot.customer) === String(ownCustomer));
        if (!belongsToThisScreen) return;
        const payoutKey = `${data.ticket}-${jackpot.type}-${jackpot.value}`;
        if (lastPayoutKey.current === payoutKey) return;
        lastPayoutKey.current = payoutKey;
        enqueuePrize({
          point: jackpot.point,
          ticket: data.ticket,
          value: jackpot.value,
          type: String(jackpot.type || '').toLowerCase(),
          customer: jackpot.customer
        });
      }
    };
    const handleSpecialBonus = (data) => {
      if (!data || data.type !== 'special-bonus' || Number(data.value) <= 0) return;
      const payoutKey = `${data.ticket || ''}-special-bonus-${data.value}`;
      if (lastPayoutKey.current === payoutKey) return;
      lastPayoutKey.current = payoutKey;
      enqueuePrize(data);
    };
    socket.on('run', handleRun);
    socket.on('get', handleGet);
    socket.on('screen', handleScreen);
    socket.on('jackpot', handleJackpot);
    socket.on('special-bonus-winner', handleSpecialBonus);
    return () => {
      socket.off('run', handleRun);
      socket.off('get', handleGet);
      socket.off('screen', handleScreen);
      socket.off('jackpot', handleJackpot);
      socket.off('special-bonus-winner', handleSpecialBonus);
      window.clearTimeout(payoutTimer.current);
      prizeQueueRef.current = [];
      paidJpRef.current = false;
      window.clearTimeout(transitionTimer.current);
      window.clearTimeout(resultTransitionTimer.current);
    };
  }, []);

  if (!localStorage.getItem('user')) {
    return window.location.href = '/';
  }

  const withLineTransition = (content) => (
    <>
      {content}
      {lineTransition > 0 && <div key={lineTransition} className="dashboard-line-transition" aria-hidden="true" />}
    </>
  );

  const withResultTransition = (content) => (
    <>
      {content}
      {resultTransition > 0 && <div key={resultTransition} className="dashboard-result-transition" aria-hidden="true" />}
    </>
  );

  // En screen 3 el servidor reserva primero 10s para el premio extraordinario;
  // al terminar la animación quedan 5s para presentar los resultados.
  if (paidJp && Number(screen) === 3) {
    return <div className="containerLine"><Jackpots key={`${jackpotToPay.type}-${jackpotToPay.ticket}-${jackpotToPay.value}`} {...jackpotToPay} /></div>;
  }

  switch (screen) {
    case 1:
      return withLineTransition(
        <div className="containerLine">
          <Lines screen={1} paleOnly={paleOnly} />
        </div>
      );
    case 2:
      return <Racing factor={run?.factor} video={run?.video} />;
    case 3:
      return withResultTransition(
        <div className="containerLine"> 
          <Lines screen={3} paleOnly={paleOnly} />
        </div>
      );
    case 4:
      if (run && run.factor && run.factor.value > 1) {
        return (
          <div className="containerLine">
            <Bonos className="position-absolute" {...run.factor} />
          </div>
        );
      } else {
        return <CountdownScreen time={time} run={run} />;
      }
    case 5:
      if (jackpotToPay.type === 'special-bonus' || jackpotToPay.type === 'general' ||
          (jackpotToPay.point && localStorage.getItem('point') && jackpotToPay.point === localStorage.getItem('point')) ||
          (jackpotToPay.type === 'CONSORCIO' && localStorage.getItem('customer') && jackpotToPay.customer === localStorage.getItem('customer'))) {
        return (
          <div className="containerLine">
            <Jackpots {...jackpotToPay} />
          </div>
        );
      } else {
        return (
          <div className="containerLine">
            <Lines screen={3} paleOnly={paleOnly} />
          </div>
        );
      }
    default:
      return <CountdownScreen time={time} run={run} />;
  }
}

export default Dashboard;
